import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { SAKTI_CONFIG_DEFAULTS } from '../apiConfig';
import SaktiApiService from '../services/saktiApiService';
import SaktiPushOdooApiService from '../services/saktiPushOdooApiService';
import { extractApiError } from '../utils/apiError';
import { getTodayInJakartaForApi } from '../utils/date';
import {
  SaktiFilterOptions,
  SaktiPushOdooFilters,
  SaktiPushOdooJob,
  SaktiPushOdooResult,
} from '../types';

// Same inclusive ceiling Sakti Go enforces; we also validate here for a fast error.
export const MAX_RANGE_DAYS = 31;

// Give up polling after this many consecutive status-less failures (network blips)
// so a dead worker never leaves the UI spinning forever.
const MAX_POLL_FAILURES = 5;

const EMPTY_OPTIONS: SaktiFilterOptions = { companies: [], estates: [], divisions: [] };

const isTerminal = (status: string): boolean =>
  status === 'completed' || status === 'failed';

const inclusiveDays = (start: string, end: string): number =>
  Math.floor((Date.parse(end) - Date.parse(start)) / 86400000) + 1;

interface UseSaktiPushOdooResult {
  options: SaktiFilterOptions;
  loadingOptions: boolean;
  filters: SaktiPushOdooFilters;
  setFilters: (next: SaktiPushOdooFilters) => void;
  job: SaktiPushOdooJob | null;
  result: SaktiPushOdooResult | null;
  isSubmitting: boolean;
  submit: () => Promise<void>;
}

export const useSaktiPushOdoo = (): UseSaktiPushOdooResult => {
  const toast = useToast();

  const today = getTodayInJakartaForApi();
  const [options, setOptions] = useState<SaktiFilterOptions>(EMPTY_OPTIONS);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(true);
  const [filters, setFilters] = useState<SaktiPushOdooFilters>({
    date_start: today,
    date_end: today,
  });
  const [job, setJob] = useState<SaktiPushOdooJob | null>(null);
  const [result, setResult] = useState<SaktiPushOdooResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const mountedRef = useRef<boolean>(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollFailuresRef = useRef<number>(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, [clearTimer]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const opts = await SaktiApiService.fetchFilterOptions();
        if (active && mountedRef.current) {
          setOptions(opts);
        }
      } catch (err) {
        if (active && mountedRef.current) {
          toast({
            title: 'Gagal memuat daftar estate',
            description: extractApiError(err).message,
            status: 'error',
            duration: 6000,
            isClosable: true,
            position: 'top-right',
          });
        }
      } finally {
        if (active && mountedRef.current) {
          setLoadingOptions(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [toast]);

  const notifyFinished = useCallback(
    (finished: SaktiPushOdooJob) => {
      if (finished.status === 'completed' && finished.result) {
        const { total_synced, total_failed } = finished.result;
        toast({
          title: total_failed > 0 ? 'Sync selesai dengan sebagian gagal' : 'Sync selesai',
          description: `${total_synced.toLocaleString('id-ID')} tersinkron, ${total_failed.toLocaleString('id-ID')} gagal.`,
          status: total_failed > 0 ? 'warning' : 'success',
          duration: 6000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }

      toast({
        title: 'Sync gagal',
        description: finished.error || 'Terjadi kesalahan saat push ke Odoo.',
        status: 'error',
        duration: 8000,
        isClosable: true,
        position: 'top-right',
      });
    },
    [toast]
  );

  const beginPolling = useCallback(
    (taskId: string) => {
      clearTimer();
      pollFailuresRef.current = 0;
      timerRef.current = setInterval(async () => {
        try {
          const updated = await SaktiPushOdooApiService.getPushJob(taskId);
          if (!mountedRef.current) {
            return;
          }

          pollFailuresRef.current = 0;
          setJob(updated);

          if (isTerminal(updated.status)) {
            clearTimer();
            setIsSubmitting(false);
            setResult(updated.result ?? null);
            notifyFinished(updated);
          }
        } catch (err) {
          if (!mountedRef.current) {
            return;
          }

          const { status, message } = extractApiError(err);
          pollFailuresRef.current = status === undefined ? pollFailuresRef.current + 1 : 0;
          // 404 = job gone (worker restarted or the poll hit a replica without the
          // in-memory job). Treat it — and any 5xx or a run of network errors — as
          // terminal, otherwise the UI spins forever on a job it can never see finish.
          const jobGone = status === 404;
          const upstreamDown = status !== undefined && status >= 500;
          const tooManyFailures = pollFailuresRef.current >= MAX_POLL_FAILURES;

          if (jobGone || upstreamDown || tooManyFailures) {
            clearTimer();
            setIsSubmitting(false);
            toast({
              title: 'Pemantauan sync terputus',
              description: jobGone
                ? 'Status job tidak lagi tersedia (worker mungkin direstart). Muat ulang halaman untuk memeriksa hasil.'
                : `Tidak dapat memantau status: ${message}. Muat ulang halaman untuk memeriksa hasil.`,
              status: 'warning',
              duration: 8000,
              isClosable: true,
              position: 'top-right',
            });
          }
        }
      }, SAKTI_CONFIG_DEFAULTS.SYNC_POLL_INTERVAL_MS);
    },
    [clearTimer, notifyFinished, toast]
  );

  const submit = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    const validationError = (() => {
      if (!filters.estate_id) return 'Pilih estate terlebih dahulu.';
      if (!filters.date_start || !filters.date_end) return 'Isi tanggal mulai dan tanggal akhir.';
      if (filters.date_end < filters.date_start) return 'Tanggal akhir harus >= tanggal mulai.';
      if (inclusiveDays(filters.date_start, filters.date_end) > MAX_RANGE_DAYS) {
        return `Rentang tanggal maksimal ${MAX_RANGE_DAYS} hari.`;
      }
      return null;
    })();

    if (validationError) {
      toast({
        title: 'Input tidak valid',
        description: validationError,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    setIsSubmitting(true);
    setResult(null);
    try {
      const created = await SaktiPushOdooApiService.startPushJob({
        estate_id: filters.estate_id as number,
        date_start: filters.date_start,
        date_end: filters.date_end,
      });
      if (!mountedRef.current) {
        return;
      }
      setJob(created);

      if (isTerminal(created.status)) {
        setIsSubmitting(false);
        setResult(created.result ?? null);
        notifyFinished(created);
      } else {
        beginPolling(created.id);
      }
    } catch (err) {
      if (!mountedRef.current) {
        return;
      }
      setIsSubmitting(false);
      toast({
        title: 'Gagal memulai sync',
        description: extractApiError(err).message,
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }, [beginPolling, filters, isSubmitting, notifyFinished, toast]);

  return {
    options,
    loadingOptions,
    filters,
    setFilters,
    job,
    result,
    isSubmitting,
    submit,
  };
};

export default useSaktiPushOdoo;
